const GRAPHQL_ENDPOINT = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000/graphql'
  : process.env.VITE_API_URL + '/graphql';

const getToken = () => localStorage.getItem('auth_token_admin');

/**
 * Helper function to make GraphQL requests
 * @param {string} query - The GraphQL query or mutation
 * @param {Object} [variables] - Optional variables for the query
 * @returns {Promise<any>} The response data
 * @throws {Error} If the request fails or returns GraphQL errors
 */
export async function fetchGraphQL(query, variables = {}) {
  const token = getToken();
  
  try {
    const response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      credentials: 'include',
      body: JSON.stringify({
        query,
        variables
      })
    });

    if (!response.ok) {
      throw new Error(`Network error: ${response.status} ${response.statusText}`);
    }

    const json = await response.json();
    
    if (json.errors) {
      console.error('GraphQL Errors:', json.errors);
      throw new Error(Array.isArray(json.errors) 
        ? json.errors[0].message 
        : 'GraphQL query failed'
      );
    }

    return json.data;
  } catch (error) {
    console.error('GraphQL Request Failed:', error);
    throw error;
  }
}

/**
 * Helper function to handle paginated GraphQL queries
 * @param {string} query - The GraphQL query
 * @param {Object} variables - Query variables including pagination params
 * @param {string} dataPath - Path to the paginated data in the response
 * @returns {Promise<any>} The paginated response data
 */
export async function fetchPaginatedGraphQL(query, variables, dataPath) {
  const data = await fetchGraphQL(query, variables);
  return dataPath.split('.').reduce((obj, key) => obj[key], data);
}

/**
 * Helper function to handle file uploads via GraphQL
 * @param {string} query - The GraphQL mutation
 * @param {Object} variables - Query variables
 * @param {File|File[]} files - File(s) to upload
 * @returns {Promise<any>} The response data
 */
export async function uploadFilesGraphQL(query, variables, files) {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();

  // Add the operations
  formData.append('operations', JSON.stringify({
    query,
    variables
  }));

  // Add the files map
  const map = {};
  const filesArray = Array.isArray(files) ? files : [files];
  filesArray.forEach((_, i) => {
    map[i] = [`variables.file${i}`];
  });
  formData.append('map', JSON.stringify(map));

  // Add the files
  filesArray.forEach((file, i) => {
    formData.append(i, file);
  });

  const response = await fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    credentials: 'include',
    body: formData
  });

  const json = await response.json();
  
  if (json.errors) {
    throw new Error(Array.isArray(json.errors) 
      ? json.errors[0].message 
      : 'File upload failed'
    );
  }

  return json.data;
} 